<template>
  <div class="content">
    <app-spinner v-if="!is_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Order (ID: {{ $route.params.id }})</h1>
          <div class="status-title" :class="[`status-title--${ order.status.code }`]">
            {{ order.status.name }}
          </div>
          <div class="status-title" :class="order.is_paid ? 'status-title--paid' : 'status-title--unpaid'">
            {{ order.is_paid ? 'Paid' : 'Unpaid' }}
          </div>
        </app-cells>
        <app-button v-if="order.status.code === 'draft'" @click.native="onShowModalDelete(null)" theme="error" ref="delete_order">
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards">
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Date:</span>
              <span>{{ dateFormat(order.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Storekeeper:</span>
              <router-link
                v-if="is_general_manager"
                :to="{ name: 'employees-profile', params: { id: order.storekeeper.id } }"
                class="info-cards__link"
              >
                {{ order.storekeeper.full_name }}
              </router-link>
              <span v-else>{{ order.storekeeper.full_name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Buyer:</span>
              <router-link
                v-if="is_general_manager"
                :to="{ name: 'employees-profile', params: { id: order.buyer.id } }"
                class="info-cards__link"
              >
                {{ order.buyer.full_name }}
              </router-link>
              <span v-else>{{ order.buyer.full_name }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row--dotted">
            <span>Estimated value:</span><span>{{ order.estimated.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Real value:</span><span>{{ order.real.toLocaleString() }} TSh</span>
          </div>
          <div class="info-cards__row info-cards__row--dotted">
            <span>Difference value:</span><span>{{ order.difference.toLocaleString() }} TSh</span>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Supplier:</span>
              <router-link
                  v-if="is_general_manager || is_product_storage_manager || is_product_storage_supervisor"
                  :to="{ name: 'supplier-detail', params: { id: order.supplier.id } }"
                  class="info-cards__link"
              >
                {{ order.supplier.name }}
              </router-link>
              <span v-else>{{ order.supplier.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Payment method:</span> <span>{{ order.payment_method.name }}</span>
            </div>
          </div>
          <div
            v-if="(order.status.code === 'in_process' || order.status.code === 'completed') && order.payment_method.code === 'cash'"
            class="info-cards__row"
          >
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Cash:</span>
              <router-link
                v-if="order.cash_request"
                :to="{ name: 'product-storage-cash-detail', params: { id: order.cash_request.id } }"
                class="info-cards__link"
              >
                {{ order.cash_request.status.name }}
              </router-link>
              <span v-else>Not requested</span>
            </div>
          </div>
          <div
            v-if="order.status.code === 'completed' && order.payment_method.code === 'bank' && showInvoice"
            class="info-cards__row"
          >
            <div class="info-cards__row-item info-cards__row-item--full info-cards__row-item--flex-end">
              <span class="info-cards__preload" :class="{'info-cards__preload--loading': file_loading && !order.invoice }">Invoice:</span>
              <template v-if="!order.invoice">
                <app-uploader
                  theme="line"
                  @onAddedFile="onUploadInvoice"
                  :extensions="['application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/zip', 'application/pdf', 'image/jpeg', 'image/png', 'image/tiff', 'image/bmp']"
                  formDataFormats="DOC,DOCX,XLS,XLSX,ZIP,PDF,JPEG,BMP,PNG,TIFF"
                />
              </template>
              <template v-else>
                <div class="info-cards__file" :class="{'info-cards__file--loading': file_loading}">
                  <button type="button" class="info-cards__file-download" @click="onDownloadInvoice">Download</button>
                  <button type="button" @click="onRemoveInvoice" class="info-cards__file-remove"></button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="order.note"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Write a notes"
          :readonly="order.status.code === 'completed'"
        />
      </app-cells>
      <v-client-table
        :data="table.items"
        :columns="table.columns"
        :options="table.options"
        class="table-default"
      >
        <template #h__product><div class="table-default__left">Product name</div></template>
        <template #count="{ row, index }">
          {{ !['draft', 'in_process'].includes(order.status.code) ? index : index !== table.items.length ? index : '' }}
        </template>
        <template #product="{ row, index }">
          <router-link
            v-if="!['draft', 'in_process'].includes(order.status.code) || (['draft', 'in_process'].includes(order.status.code) && index !== table.items.length)"
            :to="{ name: 'product-storage-products-detail', params: { id: row.product.id } }"
            class="table-default__link-border"
          >
            {{ row.product.name }}
          </router-link>
          <app-select
            v-if="['draft', 'in_process'].includes(order.status.code) && index === table.items.length"
            @option:selected="onNewProductChange"
            v-model="new_row"
            :options="products_options"
            :clearable="false"
            :calculate-position="withPopper"
            append-to-body
            placeholder="Enter product name"
            class="select--200"
            ref="new_product"
            :tabindex="1"
          />
        </template>
        <template #product.measurement.name="{ row, index }">
          {{ !['draft', 'in_process'].includes(order.status.code) ? row.product.measurement.name : index !== table.items.length ? row.product.measurement.name : new_row ? new_row.measurement.name : '' }}
        </template>
        <template #product.in_stock="{ row, index }">
          {{ !['draft', 'in_process'].includes(order.status.code) ? new Intl.NumberFormat('ru-RU').format(row.product.in_stock) : index !== table.items.length ? new Intl.NumberFormat('ru-RU').format(row.product.in_stock) : new_row ? new_row.in_stock : '' }}
        </template>
        <template #estimated_quantity="{ row, index }">
          <template v-if="order.status.code === 'draft'">
            <input
                v-if="index !== table.items.length"
                v-model.number="row.estimated_quantity"
                class="input input--150"
                @change="updateTableData(row.id, { estimated_quantity: row.estimated_quantity })"
                type="number"
                style="text-align: center"
            />
            <input
                v-if="index === table.items.length && new_row"
                @keydown.enter="onNewRowSubmit"
                v-model.number="new_row.estimated_quantity"
                class="input input--150"
                type="number"
                style="text-align: center"
                ref="new_qty"
                :tabindex="2"
            />
          </template>
          <template v-else>
            <template v-if="order.status.code === 'in_process'">
              <template v-if="index !== table.items.length">
                {{ new Intl.NumberFormat('ru-RU').format(row.estimated_quantity) }}
              </template>
              <template v-if="index === table.items.length && new_row">0</template>
            </template>
            <template v-else>
              {{ new Intl.NumberFormat('ru-RU').format(row.estimated_quantity) }}
            </template>
          </template>
        </template>
        <template #estimated_price="{ row, index }">
          <template v-if="['draft', 'in_process'].includes(order.status.code)">
            <template v-if="order.status.code === 'draft'">
              <input
                  v-if="index !== table.items.length"
                  v-model.number="row.estimated_price"
                  class="input input--150"
                  @change="updateTableData(row.id, { estimated_price: row.estimated_price })"
                  type="number"
                  style="text-align: center"
              />
              <input
                  v-if="index === table.items.length && new_row"
                  @keyup.enter="onNewRowSubmit"
                  v-model.number="new_row.price"
                  class="input input--150"
                  type="number"
                  style="text-align: center"
                  ref="new_price"
                  :tabindex="3"
              />
            </template>
            <template v-if="order.status.code === 'in_process'">
              <template v-if="index !== table.items.length">{{ row.estimated_price.toLocaleString() }}</template>
              <template v-if="index === table.items.length && new_row">{{ new_row.price.toLocaleString() }}</template>
            </template>
          </template>
          <template v-else>
            {{ row.estimated_price.toLocaleString() }}
          </template>
        </template>
        <template #value="{ row, index }">
          <template v-if="order.status.code === 'in_process'">
            <template v-if="index !== table.items.length">{{ row.value.toLocaleString() }} TSh</template>
            <template v-if="index === table.items.length && new_row">0</template>
          </template>
          <template v-if="order.status.code === 'completed'">{{ row.value.toLocaleString() }} TSh</template>
        </template>
        <template #controls="{ row, index }">
          <div v-if="index !== table.items.length" class="table-default__controls">
            <button @click="onShowModalDelete(row.id)" type="button">
              <img src="@/assets/img/delete-table-icon.svg" alt="">
            </button>
          </div>
        </template>
        <template #real_quantity="{ row, index }">
          <template v-if="order.status.code === 'in_process'">
            <input
                v-if="index !== table.items.length"
                v-model.number="row.real_quantity"
                class="input input--150"
                @change="updateTableData(row.id, { real_quantity: row.real_quantity })"
                type="number"
                style="text-align: center"
            />
            <input
                v-if="index === table.items.length && new_row"
                @keyup.enter="onNewRowSubmit"
                v-model.number="new_row.real_quantity"
                class="input input--150"
                type="number"
                style="text-align: center"
                ref="new_real_qty"
                :tabindex="4"
            />
          </template>
          <template v-else>{{ new Intl.NumberFormat('ru-RU').format( row.real_quantity) }}</template>
        </template>
        <template #different="{ row, index }">
          <template v-if="order.status.code === 'in_process'">
            <template v-if="index !== table.items.length">
              <span
                  :class="{
                    'table-default__text-green': row.different < 0,
                    'table-default__text-red': row.different > 0,
                  }"
              >
                {{ row.different }}
              </span>
            </template>
            <template v-if="index === table.items.length && new_row">0</template>
          </template>
          <span
              v-else
              :class="{
                'table-default__text-green': row.different < 0,
                'table-default__text-red': row.different > 0,
              }"
          >
            {{ row.different }}
          </span>
        </template>
        <template #real_price="{ row, index }">
          <template v-if="order.status.code === 'in_process'">
            <input
                v-if="index !== table.items.length"
                v-model.number="row.real_price"
                class="input input--150"
                @change="updateTableData(row.id, { real_price: row.real_price })"
                type="number"
                style="text-align: center"
            />
            <input
                v-if="index === table.items.length && new_row"
                @keyup.enter="onNewRowSubmit"
                v-model.number="new_row.price"
                class="input input--150"
                type="number"
                style="text-align: center"
                ref="new_real_price"
                :tabindex="5"
            />
          </template>
          <template v-else>{{ row.real_price.toLocaleString() }}</template>
        </template>
        <template #delivered="{ row, index }">
          <label
            v-if="row.real_quantity > 0"
            class="checkbox checkbox--table"
            :class="{ 'checkbox--readonly': order.status.code !== 'in_process' }"
          >
            <input
              v-model="row.delivered"
              type="checkbox"
              :name="`delivered_${index}`"
              @change="updateTableData(row.id, { delivered: row.delivered })"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
        <template #efd="{ row, index }">
          <label v-if="row.real_quantity > 0" class="checkbox checkbox--table">
            <input
              v-model="row.efd"
              type="checkbox"
              :name="`efd_${index}`"
              @change="updateTableData(row.id, { efd: row.efd })"
            />
            <span class="checkbox__checkmark"></span>
          </label>
        </template>
      </v-client-table>
      <app-cells v-if="order.status.code === 'ready'" position="end">
        <app-radio
            v-model="payment_method"
            value="cash"
            name="payment_method"
            :checked="order.payment_method.code"
            @change="onUpdatePaymentMethod"
        >
          <template #label>Pay now by cash</template>
        </app-radio>
        <app-radio
            v-model="payment_method"
            value="bank"
            name="payment_method"
            :checked="order.payment_method.code"
            @change="onUpdatePaymentMethod"
        >
          <template #label>Pay later by bank</template>
        </app-radio>
      </app-cells>
      <app-cells position="end">
        <app-button
          v-if="order.status.code === 'in_process' && ((order.payment_method.code === 'cash' && order.cash_request) || order.payment_method.code === 'bank') || order.status.code === 'completed'"
          @click.native="onPrint"
          theme="warn"
          ref="print"
        >
          Print order
        </app-button>
        <app-button
          v-for="(data, index) in status_buttons[order.status.code]"
          :key="index"
          @click.native="onShowModalUpdate(data)"
          :theme="data.modal_type === 'draft' ? 'warn' : 'default'"
          :disabled="order.status.code === 'in_process' && order.payment_method.code === 'cash' && !order.cash_request"
        >
          {{ data.button_text }}
        </app-button>
      </app-cells>
    </template>
    <modal
        name="order-update"
        :width="400"
        :height="'auto'"
        :scrollable="true"
        :adaptive="true"
        :clickToClose="false"
        class="modal-not-overflow"
        draggable=".popup__title"
        :reset="true"
    >
      <div class="popup">
        <button @click="$modal.hide('order-update')" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Update order
        </div>
        <div class="popup__body">
          <div class="popup__subt">{{ modal_data.modal_text }}</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="$modal.hide('order-update')" theme="dark">Cancel</app-button>
            <app-button @click.native="onUpdateOrderStatus(modal_data.modal_type)" ref="status_changed">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideDeleteModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete {{ product_to_delete ? 'product' : 'transfer' }}
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideDeleteModal" theme="dark">Cancel</app-button>
            <app-button v-if="product_to_delete" @click.native="onDeleteRow" ref="delete">Yes</app-button>
            <app-button v-else @click.native="onDeleteOrder" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteProductStorageOrder,
  deleteProductstorageOrderProduct,
  getOrderPrint,
  getProductsProductstorage,
  getProductStorageOrder,
  getProductstorageOrderProduct,
  patchProductStorageOrder,
  patchProductstorageOrderProduct,
  postProductstorageOrderProduct,
} from '@/http'
import { format, parse } from 'date-fns'
import { createPopper } from '@popperjs/core'
import axios from 'axios'

export default {
  name: 'ProductStorageOrdersDetail',
  data() {
    return {
      is_loaded: false,
      product_to_delete: null,
      file_loading: false,
      order: {},
      status_buttons: {
        draft: [ { button_text: 'Ready to buy', modal_type: 'ready', modal_text: 'Are you sure you want to set status Ready to buy?' } ],
        ready: [
          { button_text: 'Redraft', modal_type: 'draft', modal_text: 'Are you sure you want to set status Redraft?' },
          { button_text: 'Confirm', modal_type: 'in_process', modal_text: 'Are you sure you want to set status In process?' }
        ],
        in_process: [ { button_text: 'Complete', modal_type: 'completed', modal_text: 'Are you sure you want to complete order?' } ]
      },
      modal_data: {},
      table: {
        items: [],
        columns: [],
        options: {
          headings: {},
          pagination: { show: false },
          sortable: [],
          perPage: 1000,
          texts: {
            noResults: 'No matching entries',
          },
          rowClassCallback: () => 'table-default__row',
          resizableColumns: false
        },
      },
      columns_way_1: [
        'count',
        'product',
        'product.measurement.name',
        'product.in_stock',
        'estimated_quantity',
        'estimated_price',
        'controls'
      ],
      headings_way_1: {
        'count': '#',
        'product': 'Product name',
        'product.measurement.name': 'Unit',
        'product.in_stock': 'Curr. Stock',
        'estimated_quantity': 'Est. Qty',
        'estimated_price': 'Est. Price',
        'controls': '',
      },
      columns_way_2: [
        'count',
        'product',
        'product.measurement.name',
        'product.in_stock',
        'estimated_quantity',
        'estimated_price'
      ],
      headings_way_2: {
        'count': '#',
        'product': 'Product name',
        'product.measurement.name': 'Unit',
        'product.in_stock': 'Curr. Stock',
        'estimated_quantity': 'Est. Qty',
        'estimated_price': 'Est. Price',
      },
      columns_way_3: [
        'count',
        'product',
        'product.measurement.name',
        'product.in_stock',
        'estimated_quantity',
        'estimated_price',
        'real_quantity',
        'different',
        'real_price',
        'value',
        'delivered',
        'efd',
      ],
      headings_way_3: {
        'count': '#',
        'product': 'Product name',
        'product.measurement.name': 'Unit',
        'product.in_stock': 'Curr. Stock',
        'estimated_quantity': 'Est. Qty',
        'estimated_price': 'Est. Price',
        'real_quantity': 'Real qty',
        'different': 'Real diff.',
        'real_price': 'Real price',
        'value': 'Total value',
        'delivered': 'Product delivered',
        'efd': 'EFD',
      },
      products_options: [],
      new_row: null,
      placement: 'bottom',
      payment_method: 'cash'
    }
  },
  computed: {
    showInvoice() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_manager' || group.code === 'general_manager')
    },
    is_product_storage_manager() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_manager')
    },
    is_product_storage_supervisor() {
      return this.$store.state.userGroups.some(group => group.code === 'product_storage_supervisor')
    },
    is_general_manager() {
      return this.$store.state.userGroups.some(group => group.code === 'general_manager')
    }
  },
  created() {
    document.title = `Order (ID: ${ this.$route.params.id }) | Altezza Board`
    getProductStorageOrder(this.$route.params.id).then(res => {
      this.order = res.data

      getProductsProductstorage({ suppliers: this.order.supplier.id })
        .then(res => { this.products_options = res.data })
      getProductstorageOrderProduct(this.$route.params.id)
        .then(res => {
          this.table.items = res.data
          this.changeTableColumns(this.order.status.code, true)
          this.is_loaded = true
        })
    })
  },
  methods: {
    onPrint() {
      this.$refs.print.preload = true
      getOrderPrint(this.$route.params.id)
        .finally(() => { this.$refs.print.preload = false })
        .then(response => { window.open(response.data) })
    },
    onUpdateNotes() {
      patchProductStorageOrder(this.$route.params.id, { note: this.order.note })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Notes saved',
          })
        })
    },
    onUploadInvoice(e) {
      this.file_loading = true
      patchProductStorageOrder(this.$route.params.id, { invoice: e.id })
        .finally(() => {
          this.file_loading = false
        })
        .then(() => {
          getProductStorageOrder(this.$route.params.id).then(res => {
            this.order = res.data
            this.$notify({
              type: 'success',
              title: 'Invoice uploaded',
            })
          })
        })
    },
    onRemoveInvoice() {
      this.file_loading = true
      patchProductStorageOrder(this.$route.params.id, { invoice: null })
        .finally(() => {
          this.file_loading = false
        })
        .then(() => {
          getProductStorageOrder(this.$route.params.id).then(res => {
            this.order = res.data
            this.$notify({
              type: 'success',
              title: 'Invoice deleted',
            })
          })
        })
    },
    onDownloadInvoice() {
      this.file_loading = true
      axios.get(this.order.invoice.file, { responseType: 'blob' })
        .then(response => {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement('a')
          const extension = this.order.invoice.file_name.split('.').pop()
          link.href = url
          link.setAttribute('download', `Invoice ${this.order.supplier.name} - Order ${this.order.id}.${extension}`)
          document.body.appendChild(link)
          link.click()
          URL.revokeObjectURL(url)
          this.file_loading = false
        })
    },
    onUpdatePaymentMethod() {
      patchProductStorageOrder(this.$route.params.id, { payment_method: this.payment_method })
        .then(() => {
          getProductStorageOrder(this.$route.params.id).then(res => {
            this.order = res.data
            this.$notify({
              type: 'success',
              title: 'Payment method changed',
            })
          })
        })
    },
    onUpdateOrderStatus(status) {
      this.$refs.status_changed.preload = true
      patchProductStorageOrder(this.$route.params.id, { status })
        .finally(() => {
          this.$refs.status_changed.preload = false
          this.$modal.hide('order-update')
        })
        .then(() => {
          getProductStorageOrder(this.$route.params.id).then(res => {
            this.order = res.data
            this.$notify({
              type: 'success',
              title: 'Status changed',
            })
            this.changeTableColumns(this.order.status.code)
          })
        })
    },
    onShowModalUpdate(data) {
      this.modal_data = data
      this.$modal.show('order-update')
    },
    onShowModalDelete(id) {
      if (id) this.product_to_delete = id
      this.$modal.show('delete-modal')
    },
    onHideDeleteModal() {
      this.product_to_delete = null
      this.$modal.hide('delete-modal')
    },
    onDeleteRow() {
      this.$refs.delete.preload = true
      deleteProductstorageOrderProduct(this.$route.params.id, this.product_to_delete)
        .finally(() => {
          this.$refs.delete.preload = false
        })
        .then(res => {
          this.table.items = res.data
          this.table.items.push({})
          this.new_row = null
          getProductStorageOrder(this.$route.params.id).then(res => {
            this.order = res.data
          })
          this.$notify({
            type: 'success',
            title: 'Product deleted',
          })
          this.onHideDeleteModal()
        })
    },
    onNewRowSubmit() {
      const data = {}
      if (this.order.status.code === 'draft') {
        if (this.new_row.estimated_quantity && this.new_row.price) {
          data.product = this.new_row.id
          data.estimated_quantity = this.new_row.estimated_quantity
          data.estimated_price = this.new_row.price
          postProductstorageOrderProduct(this.$route.params.id, data)
            .then(res => {
              this.table.items = res.data
              this.table.items.push({})
              this.new_row = null
              this.$nextTick(() => {
                console.log(this.$refs.new_product)
                this.$refs.new_product.$el.querySelector('input').focus()
              })
              this.$notify({
                type: 'success',
                title: 'Product added',
              })
            })
        } else {
          this.$notify({
            type: 'info',
            title: 'Check that the table row fields are filled in correctly',
          })
        }
      } else {
        if (this.new_row.real_quantity && this.new_row.price) {
          data.product = this.new_row.id
          data.real_quantity = this.new_row.real_quantity
          data.real_price = this.new_row.price
          postProductstorageOrderProduct(this.$route.params.id, data)
            .then(res => {
              this.table.items = res.data
              this.table.items.push({})
              this.new_row = null
              this.$nextTick(() => {
                this.$refs.new_product.$el.querySelector('input').focus()
              })
              this.$notify({
                type: 'success',
                title: 'Product added',
              })
            })
        } else {
          this.$notify({
            type: 'info',
            title: 'Check that the table row fields are filled in correctly',
          })
        }
      }
    },
    changeTableColumns(status, created) {
      if (status === 'draft') {
        this.table.columns = this.columns_way_1
        this.table.options.headings = this.headings_way_1
        this.table.items.push({})
      } else if (status === 'ready') {
        if (!created) this.table.items.pop()
        this.table.columns = this.columns_way_2
        this.table.options.headings = this.headings_way_2
      } else {
        if (status === 'in_process') this.table.items.push({})
        else {
          if (!created) this.table.items.pop()
        }
        this.table.columns = this.columns_way_3
        this.table.options.headings = this.headings_way_3
      }
    },
    onNewProductChange () {
      if (this.order.status.code === 'draft') this.$nextTick(() => { this.$refs.new_qty.focus() })
      else this.$nextTick(() => { this.$refs.new_real_qty.focus() })
    },
    updateTableData(product_id, data) {
      patchProductstorageOrderProduct(this.$route.params.id, product_id, data)
        .then(res => {
          this.table.items = res.data
          if (['draft', 'in_process'].includes(this.order.status.code)) this.table.items.push({})
          getProductStorageOrder(this.$route.params.id).then(res => {
            this.order = res.data
          })
        })
    },
    onDeleteOrder() {
      this.$refs.delete.preload = true
      deleteProductStorageOrder(this.$route.params.id)
        .finally(() => {
          this.$refs.delete.preload = false
        })
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Order deleted',
          })
          this.$router.push({ name: 'product-storage-orders-active' })
        })
    },
    dateFormat(date) {
      return date ? format(parse(date, 'yyyy-MM-dd', new Date()), 'dd MMM yyyy') : ''
    },
    withPopper (dropdownList, component, {width}) {
      dropdownList.style.width = width
      createPopper(component.$refs.toggle, dropdownList, {
        placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, 0],
              adaptive: true
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn({state}) {
              component.$el.classList.toggle('drop-up', state.placement === 'top')
            },
          },
        ]
      })
    }
  }
}
</script>
